import { computed } from '@angular/core';
import { getEntry } from '@helpers/content/content';
import { isCollectibleDiscovered } from '@helpers/item/collectibles';
import { allMaps } from '@helpers/maps';
import { tiledMapGetLayer } from '@helpers/pixi/tiled-map';
import { isWorldNodeDiscovered } from '@helpers/world-node/world-node-discovery';
import { isWorldMapVisited } from '@helpers/world-node/world-map-discovery';
import type {
  CaravanContent,
  CollectibleGateContent,
  CollectibleId,
  EncounterContent,
  EncounterRandomContent,
  ExchangeNodeContent,
  GameMap,
  GatheringContent,
  NodeOverrideContent,
  OutpostContent,
  ShrineContent,
  TiledMap,
  TiledObject,
  TownContent,
  TrainerContent,
  WorldNodeEntry,
  WorldNodeLookup,
  WorldNodeNameMap,
  WorldNodePositionMap,
  WorldNodeType,
} from '@interfaces';

const NODE_LAYER_NAMES = ['Explore Nodes'];

function tiledObjectTilePosition(
  map: TiledMap,
  object: TiledObject,
): { x: number; y: number } {
  return {
    x: Math.floor(object.x / map.tilewidth),
    y: Math.floor(object.y / map.tileheight) - 1,
  };
}

export function worldNodeMapsBuild(
  maps: Map<string, GameMap>,
): WorldNodeLookup {
  const byPosition: WorldNodePositionMap = {};
  const byName: WorldNodeNameMap = {};

  maps.forEach((gameMap, mapName) => {
    const tiledMap = gameMap.data as TiledMap;

    NODE_LAYER_NAMES.forEach((layerName) => {
      const layer = tiledMapGetLayer(tiledMap, layerName);

      (layer?.objects ?? []).forEach((object) => {
        const { x, y } = tiledObjectTilePosition(tiledMap, object);
        const entry: WorldNodeEntry = {
          mapName,
          x,
          y,
          nodeName: object.name,
          nodeData: object,
        };

        byPosition[mapName] ??= {};
        byPosition[mapName][x] ??= {};
        byPosition[mapName][x][y] = entry;

        byName[object.name] = entry;
      });
    });
  });

  return { byPosition, byName };
}

export const worldNodeLookup = computed(() => worldNodeMapsBuild(allMaps()));

// Grouped once per map load (nodes never change at runtime, only visibility) instead of re-filtering all nodes on every worldNodesOfType call.
const worldNodesByType = computed(() => {
  const grouped: Partial<Record<WorldNodeType, WorldNodeEntry[]>> = {};
  Object.values(worldNodeLookup().byName).forEach((entry) => {
    const type = entry.nodeData.type as WorldNodeType;
    (grouped[type] ??= []).push(entry);
  });
  return grouped;
});

export function worldNodeAt(
  mapName: string,
  x: number,
  y: number,
): WorldNodeEntry | undefined {
  return worldNodeLookup().byPosition[mapName]?.[x]?.[y];
}

export function worldNodeByName(nodeName: string): WorldNodeEntry | undefined {
  return worldNodeLookup().byName[nodeName];
}

export function worldNodesOfType(type: WorldNodeType): WorldNodeEntry[] {
  return worldNodesByType()[type] ?? [];
}

// Returns the WorldNodeEntry (not a bare CurrentLocation) - callers need both its
// coordinates and its `.nodeName` (see worker-travel.ts).
export function kingdomNodeGet(): WorldNodeEntry | undefined {
  return worldNodesOfType('Kingdom')[0];
}

export function worldNodeEncounter(
  entry: WorldNodeEntry,
): EncounterContent | undefined {
  const content = getEntry<EncounterContent>(entry.nodeName);
  return content?.__type === 'encounter' ? content : undefined;
}

export function worldNodeGathering(
  entry: WorldNodeEntry,
): GatheringContent | undefined {
  const content = getEntry<GatheringContent>(entry.nodeName);
  return content?.__type === 'gathering' ? content : undefined;
}

export function worldNodeEncounterRandom(
  entry: WorldNodeEntry,
): EncounterRandomContent | undefined {
  const content = getEntry<EncounterRandomContent>(entry.nodeName);
  return content?.__type === 'encounterrandom' ? content : undefined;
}

export function worldNodeCaravan(
  entry: WorldNodeEntry,
): CaravanContent | undefined {
  const content = getEntry<CaravanContent>(entry.nodeName);
  return content?.__type === 'caravan' ? content : undefined;
}

export function worldNodeShrine(
  entry: WorldNodeEntry,
): ShrineContent | undefined {
  const content = getEntry<ShrineContent>(entry.nodeName);
  return content?.__type === 'shrine' ? content : undefined;
}

export function worldNodeOutpost(
  entry: WorldNodeEntry,
): OutpostContent | undefined {
  const content = getEntry<OutpostContent>(entry.nodeName);
  return content?.__type === 'outpost' ? content : undefined;
}

export function worldNodeTrainer(
  entry: WorldNodeEntry,
): TrainerContent | undefined {
  const content = getEntry<TrainerContent>(entry.nodeName);
  return content?.__type === 'trainer' ? content : undefined;
}

export function worldNodeExchange(
  entry: WorldNodeEntry,
): ExchangeNodeContent | undefined {
  const content = getEntry<ExchangeNodeContent>(entry.nodeName);
  return content?.__type === 'exchangenode' ? content : undefined;
}

export function worldNodeCollectibleGate(
  entry: WorldNodeEntry,
): CollectibleGateContent | undefined {
  const content = getEntry<CollectibleGateContent>(entry.nodeName);
  return content?.__type === 'collectiblegate' ? content : undefined;
}

export function worldNodeTown(entry: WorldNodeEntry): TownContent | undefined {
  const content = getEntry<TownContent>(entry.nodeName);
  return content?.__type === 'town' ? content : undefined;
}

// Lets a node have display text without being backed by an Encounter/Gathering entry.
export function worldNodeOverride(
  entry: WorldNodeEntry,
): NodeOverrideContent | undefined {
  const content = getEntry<NodeOverrideContent>(entry.nodeName);
  return content?.__type === 'nodeoverride' ? content : undefined;
}

// Only one content type ever matches a given node, so this `??` chain is safe.
export function isWorldNodeHidden(entry: WorldNodeEntry): boolean {
  return (
    worldNodeEncounter(entry)?.hidden ??
    worldNodeGathering(entry)?.hidden ??
    worldNodeEncounterRandom(entry)?.hidden ??
    worldNodeOverride(entry)?.hidden ??
    worldNodeTown(entry)?.hidden ??
    worldNodeShrine(entry)?.hidden ??
    worldNodeOutpost(entry)?.hidden ??
    worldNodeTrainer(entry)?.hidden ??
    worldNodeExchange(entry)?.hidden ??
    worldNodeCollectibleGate(entry)?.hidden ??
    false
  );
}

// Only one content type ever matches a given node, so this `??` chain is safe.
export function worldNodeCollectibleGateIds(
  entry: WorldNodeEntry,
): CollectibleId[] {
  return (
    worldNodeEncounter(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeGathering(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeEncounterRandom(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeCaravan(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeOverride(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeTown(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeShrine(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeOutpost(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeTrainer(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeExchange(entry)?.invisibleUntilCollectibleIdsFound ??
    worldNodeCollectibleGate(entry)?.invisibleUntilCollectibleIdsFound ??
    []
  );
}

// Nodes with no gate are always met; gated nodes need every listed collectible found.
export function isWorldNodeCollectibleGateMet(entry: WorldNodeEntry): boolean {
  return worldNodeCollectibleGateIds(entry).every((collectibleId) =>
    isCollectibleDiscovered(collectibleId),
  );
}

// Gates the map label/cursor and auto-mode targeting; non-hidden nodes are always visible.
export function isWorldNodeVisible(entry: WorldNodeEntry): boolean {
  if (!isWorldNodeCollectibleGateMet(entry)) return false;
  return !isWorldNodeHidden(entry) || isWorldNodeDiscovered(entry.nodeName);
}

// Masked to '???' when still hidden/undiscovered, so a location can't leak off-map.
export function worldNodeDisplayName(nodeName: string): string {
  const entry = worldNodeByName(nodeName);
  if (!entry) return nodeName;

  return isWorldNodeVisible(entry) ? nodeName : '???';
}

// Unplaced names (e.g. caravan traders) have no map to gate on.
export function isWorldNodeMapVisited(nodeName: string): boolean {
  const entry = worldNodeByName(nodeName);
  return !entry || isWorldMapVisited(entry.mapName);
}
